<template>
    <section class="game-cards">
        <div class="game-grid">
            <div class="game-card" v-for="game in games" :key="game.id" :class="{ open: openGame === game.id }">
                <div class="game-card-head" @click="toggle(game.id)">
                    <div class="game-card-title">
                        <h4>{{ game.name }}</h4>
                        <span class="genre-tag">{{ game.genre }}</span>
                    </div>
                    <p class="about">{{ game.about }}</p>
                    <div class="game-card-meta">
                        <span><b-icon icon="users" pack="fas" size="is-small" /> {{ playerCountLabel(game.playerCount) }}</span>
                        <span class="usage-hint"><code>/play {{ game.id }}</code></span>
                    </div>
                    <b-icon class="chevron" :icon="openGame === game.id ? 'caret-up' : 'caret-down'" pack="fas" />
                </div>

                <div class="game-card-body" v-if="openGame === game.id">
                    <p class="rules">{{ game.rules }}</p>
                    <Discord :channel="game.id" class="game-discord">
                        <component v-if="mockups[game.id]" :is="mockups[game.id]" />
                        <div class="discord-mockup-content" v-else>
                            <DiscordMessage
                                username="you"
                                role-color="#febaca"
                                :avatar="require('@/assets/images/mockup/person.jpeg')"
                                avatarExtension=".webp">
                                /play {{ game.id }}
                            </DiscordMessage>
                            <DiscordMessage
                                username="Gamebot"
                                bot
                                role-color="#52d9e9"
                                :avatar="require(`@/assets/images/brand/64w/pfp.png`)"
                                avatarExtension=".webp">
                                <DiscordMessageEmbed color="#52d9e9" :title="game.name">
                                    {{ game.about }}
                                </DiscordMessageEmbed>
                            </DiscordMessage>
                        </div>
                    </Discord>
                    <div class="usage-row">
                        <code>/play {{ game.id }}</code> to start &middot; <code>/info {{ game.id }}</code> for full rules
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
.game-cards {
    max-width: 1000px;
    margin: 0 auto;
    text-align: left;

    .game-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;

        @include mobile {
            grid-template-columns: 1fr;
        }
    }

    .game-card {
        background: white;
        border-radius: 10px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.06);
        grid-column: span 1;
        overflow: hidden;

        &.open {
            grid-column: 1 / -1;
        }
    }

    .game-card-head {
        position: relative;
        padding: 18px 44px 18px 20px;
        cursor: pointer;

        .genre-tag {
            display: inline-block;
            font-size: 0.7rem;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            color: $primary-dark;
            background: $primary-light;
            border-radius: 4px;
            padding: 2px 8px;
            margin-bottom: 6px;
        }

        h4 {
            font-weight: bold;
            font-size: 1.1rem;
            margin-bottom: 6px;
        }

        .about {
            color: $gray-dark;
            font-size: 0.9rem;
            margin-bottom: 10px;
        }

        .game-card-meta {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 0.8rem;
            color: $gray;

            .usage-hint code {
                background: $gray-light;
                border-radius: 3px;
                padding: 1px 6px;
            }
        }

        .chevron {
            position: absolute;
            top: 18px;
            right: 18px;
            color: $gray;
        }
    }

    .game-card-body {
        padding: 0 20px 24px;

        .rules {
            color: $gray-dark;
            font-size: 0.85rem;
            line-height: 1.6;
            margin-bottom: 16px;
        }

        .game-discord {
            max-width: 420px;
            margin: 0 auto 16px;
        }

        .usage-row {
            text-align: center;
            font-size: 0.85rem;
            color: $gray;

            code {
                background: $gray-light;
                border-radius: 3px;
                padding: 1px 6px;
            }
        }
    }
}
</style>

<script>
import Discord from '@/components/Discord/Discord.vue'
import DiscordMessage from '@/components/Discord/DiscordMessage.vue'
import DiscordMessageEmbed from '@/components/Discord/DiscordMessageEmbed.vue'

import Anagrams from '@/components/Mockups/Anagrams.vue'
import Chess from '@/components/Mockups/Chess.vue'
import CardsAgainstHumanity from '@/components/Mockups/CardsAgainstHumanity.vue'
import SurveySays from '@/components/Mockups/SurveySays.vue'

const GAMES = [
    { id: 'ana', name: 'Anagrams', genre: 'Word', playerCount: { min: 1, max: 12 }, about: 'Unscramble words faster than your friends!', rules: 'A set of letters is sent in the channel. Use those letters to make as many 3+ letter words as possible before time runs out. At the end, the scores are tallied up and the winner is announced.' },
    { id: 'cah', name: 'Cards Against Humanity', genre: 'Card', playerCount: { min: 3, max: 12 }, about: 'A party game for horrible people.', rules: "Each round, a black card is selected, and everyone else answers with their funniest white card. One player is the Card Czar each round, and gives a point to the funniest response." },
    { id: 'caf', name: 'Cards Against Humanity: Family Edition', genre: 'Card', playerCount: { min: 3, max: 12 }, about: 'A hilarious game for kids and adults to play together.', rules: "Same rules as Cards Against Humanity, using a family-friendly card set." },
    { id: 'che', name: 'Chess', genre: 'Board', playerCount: { min: 2, max: 2 }, about: 'Play the 500-year-old classic on Discord.', rules: "Be the first to checkmate the opponent's king." },
    { id: 'c4', name: 'Connect 4', genre: 'Tabletop', playerCount: { min: 2, max: 3 }, about: 'Classic Connect 4, but with a whole lot more.', rules: 'Players take turns dropping a colored disc into the board. Whoever gets four in a row diagonally, horizontally, or vertically wins!' },
    { id: 'oth', name: 'Othello', genre: 'Board', playerCount: { min: 2, max: 2 }, about: 'Flip over discs and capture opposing pieces in this classic strategy game.', rules: "Sandwich a row of your opponent's pieces between two of yours to flip them to your color. The game ends when neither player has a legal move." },
    { id: 'pok', name: 'Poker', genre: 'Card', playerCount: { min: 2, max: 12 }, about: 'Get straights, flushes, and full houses in this classic card game!', rules: "Texas Hold'em rules. Bet, fold, or raise across rounds as community cards are revealed. Best five-card hand wins the pot." },
    { id: 'sus', name: 'Survey Says', genre: 'Party', playerCount: { min: 3, max: 12 }, about: 'The wacky game that tells you just how weird people are.', rules: "Guess what percentage of surveyed people answered 'yes' to a question. The closest guess without going over other players wins the round." },
    { id: 'wis', name: 'Wisecracks', genre: 'Party', playerCount: { min: 3, max: 18 }, about: 'Make the wittiest responses to the silliest prompts.', rules: 'Everyone writes responses to prompts via DM. Each player judges once, picking their favorite response. Most points after all rounds wins.' },
]

export default {
    name: 'GameCards',
    components: {
        Discord,
        DiscordMessage,
        DiscordMessageEmbed,
    },
    data() {
        return {
            games: GAMES,
            openGame: null,
            mockups: {
                ana: Anagrams,
                che: Chess,
                cah: CardsAgainstHumanity,
                sus: SurveySays,
            },
        }
    },
    methods: {
        toggle(id) {
            this.openGame = this.openGame === id ? null : id
        },
        playerCountLabel(count) {
            if (count.min === count.max) return `${count.min} players`
            return `${count.min}–${count.max} players`
        },
    },
}
</script>
